import {Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {DcpAuthService} from '@app/auth/services/dcp-auth.service';
import {DcpCartConfigurationForm} from '@app/cart/dcp-cart-configuration.model';
import {OrderItemService} from '@app/checkout/order-item/services/order-item.service';
import {PrerequisiteData} from '@app/model/cart-validation/prerequisiteData';
import {DcpCartService} from '@app/product/services/dcp-cart.service';
import {EntryGroupService} from '@app/product/services/entry-group.service';
import {DcpActiveCartService} from '@app/services/dcp-active-cart.service';
import {DcpCartDecoratorService} from '@app/services/dcp-cart-decorator.service';
import {
  ConfigService,
  DcpCartDecoratorError,
  DcpCartDecoratorErrorsError,
  DcpCartDecoratorInnerError,
  DcpEntryGroup,
  DcpError,
  DcpGlobalMessageService,
  DcpPriceData,
  DcpValidation, ICartDecoratorErrorHandlingService,
  MomentService,
  TransactionListCta
} from '@dcp/configurator';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {Cart} from '@spartacus/cart/base/root';
import {OrderEntry} from '@spartacus/cart/base/root/models/cart.model';
import {GlobalMessageType, Product, TranslationService} from '@spartacus/core';
import moment from 'moment';
import {BehaviorSubject, Observable, Subject} from 'rxjs';
import {first, map, take, withLatestFrom} from 'rxjs/operators';

export const QUANTITY_OUT_OF_BOUND_ERROR: string = 'VAL_0019';
export const START_DATE_OUT_OF_BOUND_ERROR: string = 'VAL_0020';
export const ERROR_CODE_MISSING_S4M_BUY_LIC_ROLE: string = 'VAL_0036';
export const ERROR_CODE_MISSING_S4M_BUY_LIC_ROLE_NEW: string = 'VAL_0049';
export const ERROR_CODE_MISSING_S4M_BUY_LIC_ROLE_CHECKOUT: string = 'VAL_0046';
export const ERROR_CODE_PRODUCT_NOT_ALLOWED_B2I: string = 'VAL_0301';
export const ERROR_CODE_CUSTOMER_HAS_ALREADY_CPEA: string = 'VAL_0302';
export const ERROR_CODE_CUSTOMER_HAS_ALREADY_PAYG: string = 'VAL_0303';
export const ERROR_CODE_CHECKOUT_UNAVAILABLE: string = 'VAL_0304';
export const ERROR_CODE_RESTRUCTURE: string = 'VAL_0305';
export const ERROR_CODE_PREREQUISITES_REQUIRED: string = 'VAL_0040';
export const ERROR_CODE_DCP_PROMOTIONS_VALIDATION: string = 'VAL_0401';
export const CPQ_ERROR_CODE_ELIGIBLE_FOR_RETRY: string = 'DCPP_005';
export const CPQ_ERROR_CODE_PRICING: string = 'pricing:';
export const CPQ_ERROR_TYPE_FAILED_DEPENDENCY: string = 'FailedDependencyError';
export const CPQ_ERROR_TYPE_INVALID_ENTRY: string = 'InvalidEntryError';
export const TERMS_CONDITIONS_ERROR_SUBJECT_TYPE: string = 'termsconditions';
export const DATE_AUTO_CORRECTION_GROUP: string = 'dateAutoCorrection';

@UntilDestroy()
@Injectable({
  providedIn: 'root'
})
export class CartDecoratorErrorHandlingService implements ICartDecoratorErrorHandlingService {

  public errorState$ = new BehaviorSubject<DcpError>({hasError: false, isEligibleForRetry: null});
  public quantityErrorSubject = new BehaviorSubject<boolean>(false);
  public globalAccountsRoleErrorSubject = new BehaviorSubject<boolean>(false);
  public promotionRemovedErrorSubject$ = new BehaviorSubject<{ showError: boolean; validation: any }>({
    showError: false,
    validation: {}
  });
  public updatedPriceData = new Subject<DcpPriceData>();
  public disablePlaceOrder$ = new Subject<boolean>();
  private knownCodes = [
    'VAL_0001', 'VAL_0002', 'VAL_0003', 'VAL_0004', 'VAL_0005', 'VAL_0006', 'VAL_0007', 'VAL_0008', 'VAL_0009', 'VAL_0010',
    'VAL_0011', 'VAL_0012', 'VAL_0013', 'VAL_0014', 'VAL_0015', 'VAL_0016', 'VAL_0017', 'VAL_0018', 'VAL_0019', 'VAL_0020',
    'VAL_0021', 'VAL_0022', 'VAL_0023', 'VAL_0024', 'VAL_0025', 'VAL_0026', 'VAL_0027', 'VAL_0028', 'VAL_0029', 'VAL_0030',
    'VAL_0031', 'VAL_0046', 'VAL_0048', 'VAL_0100', 'VAL_0101', 'VAL_0102', 'VAL_0103', 'VAL_0105', 'VAL_0104', 'VAL_0500', 'VAL_0501',
    'VAL_0502',
  ];
  private knownCodesNoSupportLink = [
    'VAL_0046'
  ];

  constructor(
    protected activeCartService: DcpActiveCartService,
    protected authService: DcpAuthService,
    protected cartService: DcpCartService,
    protected dcpGlobalMessageService: DcpGlobalMessageService,
    protected momentService: MomentService,
    protected translation: TranslationService,
    protected entryGroupService: EntryGroupService,
    private configService: ConfigService,
    private router: Router,
    private orderItemService: OrderItemService
  ) {
  }

  public serviceErrorIsFailedDependencyError(error: DcpCartDecoratorInnerError): boolean {
    return error.errors[0].type === CPQ_ERROR_TYPE_FAILED_DEPENDENCY;
  }

  public serviceErrorIsInvalidEntryError(error: DcpCartDecoratorInnerError): boolean {
    return error.errors[0].type === CPQ_ERROR_TYPE_INVALID_ENTRY;
  }

  public serviceErrorIsEligibleForRetry(e: DcpCartDecoratorError): boolean {
    if (e.status >= 500) {
      return true;
    } else if (e.error?.errors && e.error.errors[0]) {
      if (e.status === 424) {
        return this.serviceErrorIsFailedDependencyError(e.error);
      } else if (e.status === 422) {
        if (this.serviceErrorIsInvalidEntryError(e.error)) {
          return e.error.errors[0].errorCode === CPQ_ERROR_CODE_PRICING + CPQ_ERROR_CODE_ELIGIBLE_FOR_RETRY;
        }
      }
    }

    return false;
  }

  /**
   * Handles known errors.
   *
   * @param error returned by the endpoint.
   * @param dcpCartDecoratorService helper service. It is needed to prevent a circular dependency error.
   */
  async handle(error: DcpCartDecoratorError, dcpCartDecoratorService: DcpCartDecoratorService): Promise<void> {
    if (this.configService.isKymaUrl(error?.url) || this.configService.isOccUrl(error?.url)) {
      this.dcpGlobalMessageService.addMessage(error, this?.constructor?.name, 'errorLogging');
      if (error?.error?.validation?.length) {
        for (const validation of error.error.validation) {
          await this.handleErrorByValidationCode(validation, dcpCartDecoratorService);
        }
      } else if (error?.error?.errors?.[0]) {
        const err = error.error.errors[0];
        if (err?.subjectType === TERMS_CONDITIONS_ERROR_SUBJECT_TYPE) {
          await this.handleTCError(err);
          this.disablePlaceOrder();
        } else {
          this.errorState$.next({
            ...err,
            hasError: true,
            isEligibleForRetry: this.serviceErrorIsEligibleForRetry(error),
          });
        }
      } else if (error?.status) {
        this.errorState$.next({
          ...error,
          hasError: true,
          isEligibleForRetry: this.serviceErrorIsEligibleForRetry(error),
        });
      }
      return Promise.resolve();
    }
  }

  public handleErrorByValidationCode(validation: DcpValidation, dcpCartDecoratorService: DcpCartDecoratorService): Promise<void> {
    return new Promise(resolve => {
      switch (validation.code) {
        case START_DATE_OUT_OF_BOUND_ERROR: {
          this.handleStartDateOutOfBoundInCart(validation, dcpCartDecoratorService).then(() => {
            resolve();
          });
          break;
        }
        case QUANTITY_OUT_OF_BOUND_ERROR: {
          this.quantityErrorSubject.next(true);
          resolve();
          break;
        }
        case ERROR_CODE_DCP_PROMOTIONS_VALIDATION: {
          this.translation.translate('voucher.errors.promoNoLongerValid').pipe(first(), untilDestroyed(this))
            .subscribe((msg) => {
              this.dcpGlobalMessageService.addMessage({
                  message: msg,
                  type: GlobalMessageType.MSG_TYPE_WARNING,
                  errorMessage: validation.description,
                  errorCode: validation.code,
                  showSupportLink: false
                },
                this.constructor.name, 'errorGlobal');
            });
          this.promotionRemovedErrorSubject$.next({showError: true, validation});
          resolve();
          break;
        }
        case ERROR_CODE_MISSING_S4M_BUY_LIC_ROLE: {
          this.globalAccountsRoleErrorSubject.next(true);
          resolve();
          break;
        }
        case ERROR_CODE_MISSING_S4M_BUY_LIC_ROLE_NEW: {
          this.globalAccountsRoleErrorSubject.next(true);
          resolve();
          break;
        }
        case ERROR_CODE_PREREQUISITES_REQUIRED: {
          this.disablePlaceOrder();
          for (const prerequisite of validation.context) {
            this.getPrerequisiteMessage(prerequisite.value).pipe(untilDestroyed(this)).subscribe(message => {
              this.dcpGlobalMessageService.addMessage({
                  message,
                  type: GlobalMessageType.MSG_TYPE_ERROR,
                },
                this.constructor.name,
                'errorGlobal');
            });
          }
          resolve();
          break;
        }
        default: {
          if (this.isKnownErrorCode(validation.code)) {
            this.translation.translate('checkoutReview.codes.' + validation.code, {
              code: validation.code,
            }).pipe(first(), untilDestroyed(this)).subscribe((msg) => {
              let type;
              switch (validation.severity) {
                case 'error':
                  type = GlobalMessageType.MSG_TYPE_ERROR;
                  break;
                case 'info':
                  type = GlobalMessageType.MSG_TYPE_INFO;
                  break;
                default:
                  type = GlobalMessageType.MSG_TYPE_WARNING;
                  break;
              }
              this.dcpGlobalMessageService.addMessage({
                  message: msg,
                  type,
                  errorMessage: validation.description,
                  errorCode: validation.code,
                  showSupportLink: !this.knownCodesNoSupportLink.includes(validation.code)
                },
                this.constructor.name, 'errorGlobal');
              resolve();
            });
          } else {
            this.errorState$.next({
              errorCode: validation.code,
              hasError: true,
              isEligibleForRetry: false,
              description: validation?.description
            });
            resolve();
          }
          break;
        }
      }
    });
  }

  getPrerequisiteMessage(json: string): Observable<string> {
    const messageValue: PrerequisiteData = JSON.parse(json);
    return this.translation.translate('checkoutReview.error.prerequisiteRequired.message').pipe(take(1), map(message => {
      message = message.replace('{materialName}', messageValue.materialName);

      for (const andConnection of messageValue.andConnections) {
        message += `<li>${this.buildAndOrOrString(andConnection)}</li>`;
      }
      if (messageValue.orConnections.length > 0) {
        message += '<li>';
        const orNames = messageValue.orConnections.map(value => this.buildAndOrOrString(value));
        message += orNames.join(' <b>OR</b> ');
        message += '</li>';
      }
      message += '</ul>';
      return message;
    }));
  }

  buildAndOrOrString(connection) {
    if (connection.materialNumber === 'BRACKET') {
      return `(${this.buildConnectionString(connection)})`;
    } else {
      return this.getProductName(connection);
    }
  }

  buildConnectionString(connection) {
    let message = '';
    if (connection.orConnections?.length > 0) {
      const orNames = connection.orConnections.map(value => this.buildAndOrOrString(value));
      message += orNames.join(' <b>OR</b> ');
    }
    if (connection.andConnections?.length > 0) {
      const andNames = connection.andConnections.map(value => this.buildAndOrOrString(value));
      message += andNames.join(' <b>AND</b> ');
    }
    return message;
  }

  createConnectionName(connection) {
    if (connection.targetUrl) {
      return `<a href='${connection.targetUrl}'>${connection.materialName}</a>`;
    } else {
      return connection.materialName;
    }
  }

  getProductName(connection) {
    if (connection.targetUrl) {
      return `<a href='${connection.targetUrl}'>${connection.materialName}</a>`;
    } else {
      return connection.materialName;
    }
  }

  isKnownErrorCode(code: string): boolean {
    return this.knownCodes.includes(code);
  }

  handleErrorByCtaValidation(cta: TransactionListCta) {
    this.errorState$.next({
      errorCode: cta?.error?.code,
      cta,
      hasError: true,
      isEligibleForRetry: false,
    });
  }

  public getErrorState$(): Observable<DcpError> {
    return this.errorState$.asObservable();
  }

  public quantityErrorSubject$(): Observable<boolean> {
    return this.quantityErrorSubject.asObservable();
  }

  public globalAccountsRoleErrorSubject$(): Observable<boolean> {
    return this.globalAccountsRoleErrorSubject.asObservable();
  }

  /**
   * Handles the START_DATE_OUT_OF_BOUND_ERROR for cart entries.
   *
   * @param validation contained in error message
   * @param cartDecoService dcpCartDecoratorService helper service
   */
  private async handleStartDateOutOfBoundInCart(validation: DcpValidation, cartDecoService: DcpCartDecoratorService): Promise<void> {
    return new Promise((resolve) => {
      this.activeCartService.getActiveCart().pipe(first(), untilDestroyed(this)).subscribe(async (cart) => {
        const entry: OrderEntry = this.getEntryByEntryNumber(cart, validation?.itemNumber);
        const materialEntry = this.getProductByEditionEntryNumber(cart.entryGroups, validation?.itemNumber);
        if (entry) {
          if (materialEntry?.product?.configurable) {
            this.handleCpsCartEntry(materialEntry).then(() => {
              resolve();
            });
          } else {
            this.correctCartEntry(entry, cartDecoService).then(() => {
              resolve();
            });
          }
        } else {
          resolve();
        }
      });
    });
  }

  private getEntryByEntryNumber(cart: Cart, entryNumber: string): OrderEntry {
    if (!entryNumber) {
      return undefined;
    }
    return cart.entries.find((entry) => entry?.entryNumber?.toString(10) === entryNumber);
  }

  private getProductByEditionEntryNumber(entryGroups: DcpEntryGroup[], itemNumber: string): OrderEntry {
    const commonRoot = this.entryGroupService.getRootGroupByEntryNumber(entryGroups, itemNumber);
    return commonRoot.entryGroups?.find(this.entryGroupService.isProduct).entries[0];
  }

  /**
   * Replaces incorrect cart data with valid data
   *
   * @param entry to change
   * @param dcpCartDecoratorService helper service
   */
  private async correctCartEntry(entry: OrderEntry, dcpCartDecoratorService: DcpCartDecoratorService): Promise<void> {
    return new Promise((resolve) => {
      const config: DcpCartConfigurationForm = dcpCartDecoratorService.createCartConfigurationForm(entry);
      // startDate has to be empty so cartConfigure responds the correct date
      config.data.startDate = undefined;
      dcpCartDecoratorService.cartConfigure(entry.product, config).pipe(untilDestroyed(this)).subscribe(cc => {
        this.updatedPriceData.next(cc);
        const startDate: string = cc.configuration.contract.startDatePeriod.startDate;
        const endDate: string = cc.configuration.contract.endDate;
        config.data.startDate = this.momentService.stringToNgbDateStruct(startDate);
        config.data.endDate = this.momentService.stringToNgbDateStruct(endDate);
        dcpCartDecoratorService.addToCart(entry.product, config).pipe(untilDestroyed(this)).subscribe(() => {
          this.prepareDateUpdateMessage(startDate, entry).pipe(take(1), untilDestroyed(this)).subscribe((message) => {
            this.dcpGlobalMessageService.addMessage({
                message,
                type: GlobalMessageType.MSG_TYPE_WARNING,
                group: DATE_AUTO_CORRECTION_GROUP
              },
              this.constructor.name, 'errorGlobal');
          });
          this.cartService.reloadActiveCart();
          resolve();
        });
      }, e => {
        resolve(e);
      });
    });
  }

  private handleCpsCartEntry(entry: OrderEntry): Promise<void> {
    return new Promise(resolve => {
      this.prepareDateAdjustmentMessage(entry.product).pipe(take(1), untilDestroyed(this)).subscribe((message) => {
        this.dcpGlobalMessageService.addMessage({
            message,
            type: GlobalMessageType.MSG_TYPE_ERROR,
            button: {
              isShown: true,
              id: 'edit-startDate',
              buttonText: 'checkoutReview.error.startDateNeedsAdjustment.button',
              action: () => {
                this.router.navigate([`/configure/vc/cartEntry/entityKey/${this.orderItemService.getEntityKey(entry)}`]);
              }
            }
          },
          this.constructor.name, 'errorGlobal');
      });
      resolve();
    });
  }

  /**
   * Creates the message that will be displayed to the user.
   *
   * @param startDate startDate returned by cartConfigure
   * @param entry OrderEntry containing product information
   */
  private prepareDateUpdateMessage(startDate: string, entry: OrderEntry): Observable<string> {
    return this.translation.translate('checkoutReview.startDateHasBeenUpdated', {
      date: moment(startDate).format('DD MMM YYYY'),
      productName: entry.product.name,
      productCode: entry.product.supplierAlternativeAID,
    });
  }

  private prepareDateAdjustmentMessage(product: Product): Observable<string> {
    return this.translation.translate('checkoutReview.error.startDateNeedsAdjustment.message', {
      productName: product.name
    });
  }

  private handleTCError(error: DcpCartDecoratorErrorsError): Promise<void> {
    return new Promise(resolve => {
      this.translation.translate('checkoutReview.error.termsConditions.message')
        .pipe(
          withLatestFrom(this.translation.translate('checkoutReview.error.termsConditions.secondaryMessage')),
          map(([line1, line2]) =>
            `<strong>${line1}</strong><br>${line2}`
          ),
          untilDestroyed(this))
        .subscribe(message => {
          this.dcpGlobalMessageService.addMessage({
              message,
              type: GlobalMessageType.MSG_TYPE_ERROR,
              errorMessage: error.message,
              errorCode: error.errorCode
            },
            this.constructor.name, 'errorGlobal');
          resolve();
        });
    });
  }

  public disablePlaceOrder() {
    this.disablePlaceOrder$.next(true);
  }
}
